import * as i0 from '@angular/core';
import { Injectable, InjectionToken, EventEmitter, Injector, Component, Input, Output, HostBinding, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/cdk/observers';
import { ObserversModule } from '@angular/cdk/observers';
import * as i1 from '@angular/cdk/layout';
import { Breakpoints, LayoutModule } from '@angular/cdk/layout';
import { ReplaySubject, Subject } from 'rxjs';
import { filter, takeUntil, debounceTime } from 'rxjs/operators';

var FsPaneStateChangeType;
(function (FsPaneStateChangeType) {
    FsPaneStateChangeType["Created"] = "created";
    FsPaneStateChangeType["Shown"] = "shown";
    FsPaneStateChangeType["Hidden"] = "hidden";
    FsPaneStateChangeType["Destroyed"] = "destroyed";
    FsPaneStateChangeType["ContentChanged"] = "contentChanged";
    FsPaneStateChangeType["OrientationChanged"] = "orientationChanged";
})(FsPaneStateChangeType || (FsPaneStateChangeType = {}));

class FsPaneRef {
    constructor(_paneComponent, _componentChangesNotifier$) {
        this._paneComponent = _paneComponent;
        this._componentChangesNotifier$ = _componentChangesNotifier$;
    }
    get elementRef() {
        return this._paneComponent.el.nativeElement;
    }
    contentChanged() {
        if (this._paneComponent.alive) {
            this._componentChangesNotifier$.next({
                name: this._paneComponent.name,
                type: FsPaneStateChangeType.ContentChanged,
            });
        }
    }
}

class FsPane {
    constructor(rendererFactory, _breakpointObserver) {
        this._breakpointObserver = _breakpointObserver;
        // If method create/show/hide/destroy was called - components$ event will notify all panes about this update
        this._components$ = new ReplaySubject(1, 200);
        // External notifier about updates like content, orientation and etc.
        this._componentStateChange$ = new Subject();
        this._componentsStore = new Map();
        this._componentRefsStore = new Map();
        this._renderer = rendererFactory.createRenderer(null, null);
        this._listenHandsetChange();
    }
    get components$() {
        return this._components$.asObservable();
    }
    // Listen component changes
    componentChanges$(name) {
        return this._componentStateChange$.pipe(filter((event) => event.name === name));
    }
    // Listen all components changes
    componentsChanges$() {
        return this._componentStateChange$.asObservable();
    }
    /**
     * If passed event type related with content modifiers. Ex: destroy or hide it is not modifier
     * @param eventType
     */
    paneModifiedType(eventType) {
        return [
            FsPaneStateChangeType.Created,
            FsPaneStateChangeType.OrientationChanged,
            FsPaneStateChangeType.Shown,
            FsPaneStateChangeType.ContentChanged,
        ].indexOf(eventType) > -1;
    }
    /**
     * Create new pane with passed component
     * @param name
     * @param component
     * @param data
     */
    create(name, component, data) {
        if (this._componentsStore.has(name)) {
            this._componentsStore.delete(name);
        }
        this._componentsStore.set(name, {
            component: component,
            data: data,
            hidden: false,
        });
        this._addComponentGlobalClass(name);
        this._updateGlobalClass();
        this._updateComponents();
        this._changeState(name, FsPaneStateChangeType.Created);
        return this.getPaneRef(name);
    }
    /**
     * Full destroy pane
     * @param name
     */
    destroy(name) {
        if (this._componentsStore.has(name)) {
            this._componentsStore.delete(name);
        }
        this._removeComponentGlobalClass(name);
        this._updateGlobalClass();
        this._updateComponents();
        this._changeState(name, FsPaneStateChangeType.Destroyed);
    }
    /**
     * Show pane
     * @param name
     */
    show(name) {
        if (this._componentsStore.has(name)) {
            this._componentsStore.get(name).hidden = false;
        }
        this._addComponentGlobalClass(name);
        this._updateGlobalClass();
        this._updateComponents();
        this._changeState(name, FsPaneStateChangeType.Shown);
        return this.getPaneRef(name);
    }
    /**
     * Hide pane
     * @param name
     */
    hide(name) {
        if (this._componentsStore.has(name)) {
            this._componentsStore.get(name).hidden = true;
        }
        this._removeComponentGlobalClass(name);
        this._updateGlobalClass();
        this._updateComponents();
        this._changeState(name, FsPaneStateChangeType.Hidden);
    }
    /**
     * Get pane reference to listen
     * @param name
     */
    getPaneRef(name) {
        return this._componentRefsStore.get(name);
    }
    /**
     * Register FsPane component container and create Pane Reference
     * @param name
     * @param component
     */
    registerPaneComponent(name, component) {
        if (this._componentRefsStore.has(name)) {
            throw Error(`FsPane component with name ${name} already exists!`);
        }
        const paneRef = new FsPaneRef(component, this._componentStateChange$);
        this._componentRefsStore.set(name, paneRef);
        return paneRef;
    }
    /**
     * Remove pane component
     * @param name
     */
    removePaneComponent(name) {
        this._componentRefsStore.delete(name);
    }
    /**
     * Notify that state for component was changed with some type
     * @param name
     * @param type
     */
    _changeState(name, type) {
        this._componentStateChange$.next({
            name,
            type,
        });
    }
    _updateGlobalClass() {
        const hasOpenedPanes = Array.from(this._componentsStore.values()).some((component) => {
            return !component.hidden;
        });
        if (hasOpenedPanes) {
            this._renderer.addClass(document.body, 'fs-pane-open');
        }
        else {
            this._renderer.removeClass(document.body, 'fs-pane-open');
        }
    }
    _addComponentGlobalClass(name) {
        this._renderer.addClass(document.body, `fs-pane-open-${name}`);
    }
    _removeComponentGlobalClass(name) {
        this._renderer.removeClass(document.body, `fs-pane-open-${name}`);
    }
    _updateComponents() {
        this._components$.next(this._componentsStore);
    }
    /**
     * Orientation changes
     */
    _listenHandsetChange() {
        this._breakpointObserver.observe([
            Breakpoints.HandsetLandscape,
            Breakpoints.HandsetPortrait
        ]).subscribe(result => {
            this._componentsStore.forEach((el, key) => {
                if (!el.hidden) {
                    this._changeState(key, FsPaneStateChangeType.OrientationChanged);
                }
            });
        });
    }
}
FsPane.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPane, deps: [{ token: i0.RendererFactory2 }, { token: i1.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Injectable });
FsPane.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPane });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPane, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.RendererFactory2 }, { type: i1.BreakpointObserver }]; } });

/** Injection token that can be used to access the data that was passed in to a drawer. */
const PANE_DATA = new InjectionToken('fs.pane-data');

class FsPaneComponent {
    constructor(el, _injector, _pane, _contentObserver) {
        this.el = el;
        this._injector = _injector;
        this._pane = _pane;
        this._contentObserver = _contentObserver;
        this.visibilityChanged = new EventEmitter();
        this.hidden = false;
        this.alive = false;
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this._subscribeToUpdates();
        this._pane.registerPaneComponent(this.name, this);
        this._contentObserver.observe(this.el.nativeElement)
            .pipe(takeUntil(this._destroy$), debounceTime(300))
            .subscribe(() => {
            const paneRef = this._pane.getPaneRef(this.name);
            if (paneRef) {
                paneRef.contentChanged();
            }
        });
    }
    ngOnDestroy() {
        this._pane.removePaneComponent(this.name);
        this._destroy$.next();
        this._destroy$.complete();
    }
    _subscribeToUpdates() {
        this._pane.components$
            .pipe(takeUntil(this._destroy$))
            .subscribe((components) => {
            this._componentsChanged(components);
        });
    }
    _componentsChanged(components) {
        const componentData = components.get(this.name);
        if (componentData) {
            // Create component
            if (!this.alive) {
                this._initInjector(componentData.data);
                this.component = componentData.component;
                this.alive = true;
                this.hidden = componentData.hidden;
            }
            else {
                this.hidden = componentData.hidden;
            }
        }
        else {
            // Destroy component by ngIf
            if (this.alive) {
                this.alive = false;
            }
        }
    }
    _initInjector(data) {
        this.componentInjector = Injector.create({
            providers: [
                {
                    provide: PANE_DATA,
                    useValue: data,
                }
            ],
            parent: this._injector,
        });
    }
}
FsPaneComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPaneComponent, deps: [{ token: i0.ElementRef }, { token: i0.Injector }, { token: FsPane }, { token: i2.ContentObserver }], target: i0.ɵɵFactoryTarget.Component });
FsPaneComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsPaneComponent, selector: "fs-pane", inputs: { name: "name" }, outputs: { visibilityChanged: "visibilityChanged" }, host: { properties: { "class.hidden": "this.hidden" } }, ngImport: i0, template: "<ng-container *ngIf=\"alive\">\n  <ng-template [ngComponentOutlet]=\"component\" [ngComponentOutletInjector]=\"componentInjector\"></ng-template>\n</ng-container>\n", styles: [":host{display:block}:host.hidden{display:none}\n"], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgComponentOutlet, selector: "[ngComponentOutlet]", inputs: ["ngComponentOutlet", "ngComponentOutletInjector", "ngComponentOutletContent", "ngComponentOutletNgModuleFactory"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPaneComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-pane',
                    templateUrl: 'pane.component.html',
                    styleUrls: ['pane.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Injector }, { type: FsPane }, { type: i2.ContentObserver }]; }, propDecorators: { name: [{
                type: Input
            }], visibilityChanged: [{
                type: Output
            }], hidden: [{
                type: HostBinding,
                args: ['class.hidden']
            }] } });

class FsPaneModule {
    static forRoot() {
        return {
            ngModule: FsPaneModule,
            providers: [FsPane]
        };
    }
}
FsPaneModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPaneModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsPaneModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPaneModule, declarations: [FsPaneComponent], imports: [CommonModule,
        ObserversModule,
        LayoutModule], exports: [FsPaneComponent] });
FsPaneModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPaneModule, imports: [[
            CommonModule,
            ObserversModule,
            LayoutModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPaneModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ObserversModule,
                        LayoutModule,
                    ],
                    exports: [
                        FsPaneComponent,
                    ],
                    entryComponents: [],
                    declarations: [
                        FsPaneComponent,
                    ],
                }]
        }] });

/*
 * Public API Surface of fs-menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FsPane, FsPaneComponent, FsPaneModule, FsPaneStateChangeType, PANE_DATA };
